import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayResource, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React, { useMemo } from "react";
import { ArgoCDGatewayHealthCallout } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDGatewayHealthCallout";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import Section from "~/components/Section";
import { DataTable, DataTableBody, DataTableRow, DataTableRowColumn, DataTableRowHeaderColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
import type { ArgoCDInstancePageLoaderData } from "./ArgoCDInstancePageLoaderData";
type ArgoCDInstanceConnectivityPageProps = {
    loaderData: ArgoCDInstanceConnectivityPageLoaderData;
};
export type ArgoCDInstanceConnectivityPageLoaderData = ArgoCDInstancePageLoaderData;
export async function argoCDInstanceConnectivityPageLoader(_: Repository, gatewayPromise: Promise<ArgoCDGatewayResource>): Promise<ArgoCDInstanceConnectivityPageLoaderData> {
    const gateway = await gatewayPromise;
    return { gateway };
}
export const ArgoCDInstanceConnectivityPage = ({ loaderData }: ArgoCDInstanceConnectivityPageProps) => {
    const { gateway } = loaderData;
    const { navigate } = useSpaceAwareNavigation();
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create ArgoCDGatewayHealthCheck task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForArgoCDGateway(gateway),
        afterComplete: async (_, createdTask) => {
            navigate(links.taskPage.generateUrl({ taskId: createdTask.Id }));
        },
    });
    const dataRows = useMemo(() => {
        if (!gateway.InstallDetails) {
            return [];
        }
        return [
            ["Gateway Version", gateway.InstallDetails.GatewayVersion],
            ["Helm Chart Version", gateway.InstallDetails.HelmChartVersion],
            ["Helm Release Name", gateway.InstallDetails.HelmReleaseName],
            ["Namespace", gateway.InstallDetails.KubernetesNamespace],
            ["Argo CD Version", gateway.InstallDetails.ArgoCDVersion ?? "Unknown"],
        ];
    }, [gateway]);
    return (<PageContent header={{
            title: "Gateway Connectivity",
            primaryAction: {
                type: "button",
                label: "Check health",
                onClick: createHealthCheckTask,
                disabled: isCreatingHealthCheckTask,
            },
        }}>
            <Section>
                <ArgoCDGatewayHealthCallout gateway={gateway}/>
            </Section>
            {gateway.InstallDetails && (<Section sectionHeader={"Details"}>
                    <DataTable>
                        <DataTableBody>
                            {dataRows.map(([header, value]) => (<DataTableRow key={header}>
                                    <DataTableRowHeaderColumn>{header}</DataTableRowHeaderColumn>
                                    <DataTableRowColumn>{value}</DataTableRowColumn>
                                </DataTableRow>))}
                        </DataTableBody>
                    </DataTable>
                </Section>)}
        </PageContent>);
};
